import { useCallback, useEffect, useState } from "react";
import { Outlet, useParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { TabNav } from "@/components/TabNav";
import { useApi } from "@/lib/api";
import type { PagedResult } from "@/lib/pagination";
import { EntityDetailHeader } from "./EntityDetailHeader";

interface Gateway {
  id: string;
  name: string;
  source: string;
  lastSeenAt: string | null;
  platform: string | null;
}

export interface GatewayLayoutContext {
  onGatewayRenamed: (name: string) => void;
  resourceCount: number;
}

export function GatewayLayout() {
  const { accessToken } = useAuth();
  const api = useApi();
  const { orgId, gatewayId } = useParams();
  const [gateway, setGateway] = useState<Gateway | null>(null);
  const [resourceCount, setResourceCount] = useState<number | undefined>(
    undefined,
  );

  const load = useCallback(async () => {
    if (!accessToken || !orgId || !gatewayId) {
      return null;
    }

    const [data, resources] = await Promise.all([
      api.get<Gateway>(`/organizations/${orgId}/gateways/${gatewayId}`),
      api.get<PagedResult<unknown>>(
        `/organizations/${orgId}/gateways/${gatewayId}/resources?page=1&pageSize=1`,
      ),
    ]);

    return { gateway: data, resourceCount: resources?.totalCount };
  }, [accessToken, api, orgId, gatewayId]);

  useEffect(() => {
    let cancelled = false;

    void load().then((result) => {
      if (!cancelled && result) {
        setGateway(result.gateway);
        setResourceCount(result.resourceCount);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [load]);

  function onGatewayRenamed(name: string) {
    setGateway((current) => (current ? { ...current, name } : current));
  }

  return (
    <div className="mx-auto max-w-3xl text-foreground">
      <EntityDetailHeader
        backTo={`/${orgId}/networking/gateways`}
        backLabel="Gateways"
        name={gateway?.name ?? null}
        lastSeenAt={gateway?.lastSeenAt ?? null}
        facts={[
          gateway
            ? gateway.source === "Hosted"
              ? "Hosted"
              : "Self-hosted"
            : null,
          gateway?.platform ?? null,
        ]}
      />

      <TabNav
        tabs={[
          { label: "Details", to: "details" },
          { label: "Resources", to: "resources", count: resourceCount },
          ...(gateway?.source === "SelfHosted"
            ? [{ label: "Settings", to: "settings" }]
            : []),
        ]}
        basePath={`/${orgId}/networking/gateways/${gatewayId}`}
      />

      <Outlet
        context={
          {
            onGatewayRenamed,
            resourceCount: resourceCount ?? 0,
          } satisfies GatewayLayoutContext
        }
      />
    </div>
  );
}
